<template>
    <div :class="stylePanel">
        <CompassPanel
            :ref="refPanel"
            v-model="showPanel"
            size="large"
            :title="subject"
            icon="obj-slsy-sent-email"
            enableShrink
            @toggled="toggleState"
            @closed="closePanel"
        >
            <CompassLoader v-if="loading" size="large" class="loader" />
            <div v-else v-show="!isPanelMinimized" class="email-panel__content">
                <CompassAlert
                    v-if="error"
                    :description="error.description"
                    :action="error.action"
                    :showClose="false"
                    type="error"
                />

                <EmailConsultPanelItem
                    v-for="(item, index) in olderThreads"
                    :key="item.id"
                    :email="item"
                    :thread="thread"
                    :showContent="olderThreads.length > 1 && showEmailThreadContent(index)"
                    :unique="false"
                    :canSendEmail="canSendEmail"
                    @reply="handleReply(item)"
                />
                <EmailConsultPanelEditor
                    v-if="isEditing"
                    :ref="refPanelEditor"
                    :hashPanel="hashPanel"
                    :recipients="recipients"
                    :thread="thread"
                    :threadId="decodedThreadId"
                    :replyContent="replyContent"
                    :replyType="replyType"
                    :forward="forward"
                    :signature="signature"
                    :prefillContent="description"
                    :prefillAttachments="attachments"
                    :sending_type="sending_type"
                    :objectid="objectid"
                    :providerThreadId="isFromProvider ? threadId : null"
                    :isDraft="isDraft"
                    @handleClose="closePanel"
                    @delete="handleDelete"
                    @change="handleChange"
                />
                <EmailConsultPanelItem
                    v-for="(item, index) in currentAndRecentThreads"
                    :key="item.id"
                    :email="item"
                    :thread="thread"
                    :showContent="showEmailThreadContent(index)"
                    :unique="thread.length === 1"
                    :isCurrentEmail="checkIsCurrentEmail(item)"
                    :canSendEmail="canSendEmail"
                    @reply="handleReply(item)"
                />
            </div>
            <template #footer>
                <div v-if="!isPanelMinimized && !isEditing" class="email-panel__footer">
                    <div class="compass-panel__footer--default">
                        <CompassTooltip
                            :disabled="canSendEmail"
                            :content="$t.__('email.EmailPanel.error_reconnect_email_account')"
                            placement="top"
                        >
                            <CompassButton
                                :label="$t.__('common.actions.transfer')"
                                type="secondary"
                                size="medium"
                                leftIcon="forward"
                                :disabled="loading || !canSendEmail"
                                @click="handleForward"
                            />
                        </CompassTooltip>

                        <CompassTooltip
                            :disabled="canSendEmail"
                            :content="$t.__('email.EmailPanel.error_reconnect_email_account')"
                            placement="top"
                        >
                            <CompassButton
                                :label="$t.__('email.EmailConsultPanel.reply_all')"
                                type="secondary"
                                size="medium"
                                leftIcon="reply-all"
                                :disabled="loading || !canSendEmail"
                                @click="handleReplyAll"
                            />
                        </CompassTooltip>

                        <CompassTooltip
                            :disabled="canSendEmail"
                            :content="$t.__('email.EmailPanel.error_reconnect_email_account')"
                            placement="top-end"
                        >
                            <CompassButton
                                :label="$t.__('common.actions.answer')"
                                type="secondary"
                                size="medium"
                                leftIcon="reply"
                                :disabled="loading || !canSendEmail"
                                @click="handleReply(emailInfo)"
                            />
                        </CompassTooltip>
                    </div>
                </div>
            </template>
        </CompassPanel>
    </div>
</template>
<script>
import { getEmailThreadById } from 'api/emailEngine/emailEngineService';
import EmailConsultPanelItem from 'vcomponents/email/EmailConsultPanelItem.vue';
import EmailConsultPanelEditor from 'vcomponents/email/EmailConsultPanelEditor.vue';
import { createNamespacedHelpers } from 'vuex';
import {
    getEmailSettings,
    getRecomendUsers,
    getProviderThreadMessages,
} from 'api/emailEngine/emailEngineService';
import RichTextMixin from 'mixins/RichTextMixin';

const { mapActions } = createNamespacedHelpers('user');
const { mapState: mapStateUser } = createNamespacedHelpers('user');

const ACTION_TYPE = {
    REPLY: 'reply',
    REPLY_ALL: 'reply-all',
    FORWARD: 'forward',
};

export default {
    components: {
        EmailConsultPanelItem,
        EmailConsultPanelEditor,
    },
    mixins: [RichTextMixin],
    props: {
        isPanelMinimized: {
            type: Boolean,
            default: false,
        },
        threadId: {
            type: String,
            default: '',
        },
        messageId: {
            type: String,
            default: '',
        },
        hashPanel: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            default: '',
        },
        prefillAttachments: {
            type: Array,
            default: () => [],
        },
        prefillSubject: {
            type: String,
            default: '',
        },
        sending_type: {
            type: String,
            default: '',
        },
        prefillEmailTo: {
            type: Array,
            default: () => [],
        },
        objectid: {
            type: String,
            default: '',
        },
        linkedId: {
            type: [String, Number],
            default: '',
        },
        linkedType: {
            type: String,
            default: '',
        },
        isFromProvider: {
            type: Boolean,
            default: false,
        },
        readProviderThreadLink: {
            type: String,
            default: '',
        },
    },
    emits: ['close-panel', 'toggle-panel-state'],
    data() {
        return {
            recommendedUsers: [],
            objectTypeMapper: {
                third: 'company',
                people: 'contact',
                opportunity: 'opportunity',
            },
            replyType: null,
            refPanel: null,
            refPanelEditor: null,
            showPanel: false,
            loading: false,
            isEditing: false,
            isDraft: false,
            thread: [],
            recipients: {
                to: [],
                cc: [],
                bcc: [],
            },
            emailInfo: [],
            replyContent: '',
            signature: '',
            emailConnection: '',
            forward: false,
            emailsTo: [],
            isSetupEmail: false,
            canSendEmail: true,
            error: null,
            contactsToAddAutomatically: [],
            attachments: [],
        };
    },
    computed: {
        ...mapStateUser({
            userForename: 'forename',
            name: 'name',
            userEmail: 'email',
        }),
        decodedThreadId() {
            return this.thread.length ? atob(this.thread[0].thread_id) : '';
        },
        currentThreadCreatedDate() {
            return moment(this.emailInfo.created).format();
        },
        filterThread() {
            return this.thread.filter((email, index) => !this.isEditing || index !== 0);
        },
        olderThreads() {
            return this.filterThread
                .filter((email) => moment(email.created).isBefore(this.currentThreadCreatedDate))
                .reverse();
        },
        currentAndRecentThreads() {
            return this.filterThread.filter((email) =>
                moment(email.created).isSameOrAfter(this.currentThreadCreatedDate),
            );
        },
        subject() {
            return (this.prefillSubject || this.emailInfo?.subject) ?? '';
        },
        stylePanel() {
            return ['email-panel', { minimized: this.isPanelMinimized }];
        },
        showEmailThreadContent() {
            return (index) => {
                if (this.isEditing) {
                    return false;
                }
                return index === 0;
            };
        },
    },
    watch: {
        isPanelMinimized(newValue) {
            this.$nextTick(() => {
                this.$refs[`${this.refPanel}`].isMinimized = newValue;
            });
        },
    },
    created() {
        if (this.threadId) {
            this.loading = true;
            this.getSettings();

            if (!this.isFromProvider) {
                getEmailThreadById(this.threadId)
                    .then((res) => {
                        const index = res.emails.findIndex((email) => email.id === this.messageId);
                        if (index > 0) {
                            const [emailToMove] = res.emails.splice(index, 1);
                            res.emails.unshift(emailToMove);
                        }
                        this.thread = res.emails;
                        this.emailInfo = res.emails[0];
                        this.loadEmailData();
                    })
                    .catch((error) => {
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            text: error,
                        });
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            } else {
                getProviderThreadMessages(this.readProviderThreadLink)
                    .then((response) => {
                        const formattedProviderEmails = [];
                        for (const email of response.data) {
                            formattedProviderEmails.push(this.formatProviderEmail(email));
                        }

                        this.thread = formattedProviderEmails;
                        this.emailInfo = formattedProviderEmails[0];
                        this.loadEmailData();
                    })
                    .catch((error) => {
                        switch (error.response.status) {
                            case 429:
                            case 504:
                                this.error = {
                                    description: error.response.statusText,
                                    action: {
                                        label: this.$t.__('common.labels.contact_support'),
                                        callback: () => window.Intercom('show'),
                                    },
                                };
                                break;
                            case 400:
                            case 401:
                            case 404:
                            default:
                                this.error = {
                                    description: this.$t.__(
                                        'email.EmailConsultPanel.error_loading_email',
                                    ),
                                };
                        }
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        }
        this.refPanel = `refPanel-${this.hashPanel}`;
        this.refPanelEditor = `refEmailPanelRichText-${this.hashPanel}`;
        this.manageMinimize();
    },
    mounted() {
        this.showPanel = true;
        this.loadrecommendedUsers();
        this.manageMinimize();
        if (this.sending_type) {
            this.emailsTo = this.prefillEmailTo;
            this.replyContent = this.description;
            this.attachments = this.prefillAttachments;

            switch (this.sending_type) {
                case ACTION_TYPE.REPLY_ALL:
                    this.handleReplyAll();
                    break;
                case ACTION_TYPE.FORWARD:
                    this.handleForward();
                    break;
                case ACTION_TYPE.REPLY:
                default:
                    this.handleReply(this.emailInfo);
            }
        }
    },
    methods: {
        ...mapActions(['setRecommendedUsers']),
        loadrecommendedUsers() {
            const objectType = this.$route.name?.split('.')[0];
            if (objectType === 'emails') {
                return;
            }

            const data = {
                id: this.linkedId ?? parseInt(this.$route.params.id),
                type: this.linkedType ?? this.objectTypeMapper[objectType],
            };
            getRecomendUsers(data.id, data.type).then((response) => {
                if (response.data) {
                    const mappedData = response.data.map((item) => ({
                        email: item.email,
                        firstName: item.name,
                        lastName: '',
                        position: item.relatedObject.type === 'staff' ? null : item.position,
                        id: item.relatedObject.id,
                        type: item.relatedObject.type,
                        avatar: item.avatar,
                    }));
                    this.setRecommendedUsers(mappedData);
                }
            });
        },
        manageMinimize() {
            if (this.isPanelMinimized) {
                this.$nextTick(() => {
                    this.$refs[`${this.refPanel}`].isMinimized = true;
                });
            }
        },
        getSettings() {
            this.canSendEmail = false;
            getEmailSettings()
                .then((response) => {
                    const { connection, signature, add_sender_to_email } = response.data;

                    if (connection) {
                        if (connection.type && connection.type === 'transfer') {
                            this.$router.push({
                                name: 'onboarding.email',
                                query: {
                                    invalid_onboarding: 'transfer',
                                },
                            });
                        }

                        this.canSendEmail = true;

                        if (connection.email) {
                            this.emailConnection = connection.email;
                            this.isSetupEmail = true;

                            if (signature?.computed) {
                                this.signature = signature.computed;
                            }
                        }
                    }

                    if (add_sender_to_email) {
                        const emailToAdd = connection.email ?? this.userEmail;
                        this.contactsToAddAutomatically.push({
                            email: emailToAdd,
                            isValid: this.checkEmailValidation(emailToAdd),
                            name: this.userForename,
                        });

                        this.addAutomaticContactsToRecipient();
                    }
                })
                .catch((error) => {
                    this.canSendEmail = false;
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: error,
                    });
                });
        },
        toggleState() {
            this.$emit('toggle-panel-state');
        },
        checkEmailValidation(value) {
            const regex = /^((?!\.)[\w-_.]*[^.])(@\w+)(\.\w+(\.\w+)?[^.\W])$/gim;
            return !!regex.test(value.toLowerCase());
        },
        generatEmailContentForReply(emailToReply) {
            this.replyContent = `<p></p><hr>
                <p>${this.$t.__('email.EmailConsultPanel.replied_message.title', {
                    date: this.formattedSentDate(emailToReply.created),
                    senderName: emailToReply.from.name ? emailToReply.from.name : '',
                    senderEmail: emailToReply.from.email,
                })}</p>
                <blockquote><p>${emailToReply.content}</p></blockquote>`;
            this.emailInfo.subject = emailToReply.subject;
        },
        generatEmailContentForward(emailToForward) {
            this.replyContent = `<p></p><hr>
                <p>${this.$t.__('email.EmailConsultPanel.forwarded_message.title')}</p>
                <p>${this.$t.__('email.EmailConsultPanel.forwarded_message.from', {
                    senderName: emailToForward.from.name ? emailToForward.from.name : '',
                    senderEmail: emailToForward.from.email,
                })}</p>
                <p>${this.$t.__('email.EmailConsultPanel.forwarded_message.to', {
                    recipientName:
                        emailToForward.to[0] && emailToForward.to[0].name
                            ? emailToForward.to[0].name
                            : '',
                    recipientEmail:
                        emailToForward.to[0] && emailToForward.to[0].email
                            ? emailToForward.to[0].email
                            : '',
                })}</p>
                <p>${this.$t.__('email.EmailConsultPanel.forwarded_message.date', {
                    date: this.formattedSentDate(emailToForward.created),
                })}</p>
                <p>${this.$t.__('email.EmailConsultPanel.forwarded_message.object', {
                    subject: emailToForward.subject,
                })}</p>
                <blockquote><p>${emailToForward.content}</p></blockquote>`;
        },
        formatGeneratedEmail(contact) {
            if (!this.sending_type) {
                return {
                    email: contact.email,
                    isValid: this.checkEmailValidation(contact.email),
                    name: contact.name ?? contact.email,
                    related: this.emailInfo.related,
                };
            }

            return contact;
        },
        addAutomaticContactsToRecipient() {
            if (this.contactsToAddAutomatically.length === 0) {
                return;
            }

            for (const contactToAddAutomatically of this.contactsToAddAutomatically) {
                if (
                    !this.checkContactAlreadyExistsInContactsList(
                        contactToAddAutomatically,
                        this.recipients.bcc,
                    )
                ) {
                    this.recipients.bcc.push(contactToAddAutomatically);
                }
            }
        },
        handleForward() {
            this.resetRecipients();

            if (!this.sending_type) {
                this.generatEmailContentForward(this.emailInfo);
            }

            this.replyType = ACTION_TYPE.FORWARD;
            this.forward = true;
            this.isEditing = true;
        },
        handleReply(emailToReply) {
            this.resetRecipients();
            this.recipients.to = [...this.emailsTo];

            if (emailToReply.from.email === this.emailConnection) {
                for (const contact of emailToReply.to) {
                    const contactFormated = this.formatGeneratedEmail(contact);
                    if (
                        !this.checkContactAlreadyExistsInContactsList(
                            contactFormated,
                            this.recipients.to,
                        )
                    ) {
                        this.recipients.to.push(contactFormated);
                    }
                }
            } else {
                this.recipients.to.push(this.formatGeneratedEmail(emailToReply.from));
            }

            if (!this.sending_type) {
                this.generatEmailContentForReply(emailToReply);
            }

            this.replyType = ACTION_TYPE.REPLY;
            this.forward = false;
            this.isEditing = true;
        },
        handleReplyAll() {
            this.resetRecipients();
            this.recipients.to = [...this.emailsTo];

            for (const thread of this.thread) {
                const fromContactFormated = this.formatGeneratedEmail(thread.from);
                if (
                    fromContactFormated.email !== this.emailConnection &&
                    !this.checkContactAlreadyExistsInContactsList(
                        fromContactFormated,
                        this.recipients.to,
                    )
                ) {
                    this.recipients.to.push(fromContactFormated);
                }

                for (const contact of thread.to) {
                    const contactFormated = this.formatGeneratedEmail(contact);
                    if (
                        contactFormated.email !== this.emailConnection &&
                        !this.checkContactAlreadyExistsInContactsList(
                            contactFormated,
                            this.recipients.to,
                        )
                    ) {
                        this.recipients.to.push(contactFormated);
                    }
                }

                for (const contact of thread.cc) {
                    const contactFormated = this.formatGeneratedEmail(contact);
                    if (
                        !this.checkContactAlreadyExistsInContactsList(
                            contactFormated,
                            this.recipients.to,
                        ) &&
                        !this.checkContactAlreadyExistsInContactsList(
                            contactFormated,
                            this.recipients.cc,
                        )
                    ) {
                        this.recipients.cc.push(contactFormated);
                    }
                }

                for (const contact of thread.bcc) {
                    const contactFormated = this.formatGeneratedEmail(contact);
                    if (
                        !this.checkContactAlreadyExistsInContactsList(
                            contactFormated,
                            this.recipients.to,
                        ) &&
                        !this.checkContactAlreadyExistsInContactsList(
                            contactFormated,
                            this.recipients.bcc,
                        )
                    ) {
                        this.recipients.bcc.push(contactFormated);
                    }
                }
            }

            if (!this.sending_type) {
                this.generatEmailContentForReply(this.emailInfo);
            }

            this.replyType = ACTION_TYPE.REPLY_ALL;
            this.forward = false;
            this.isEditing = true;
        },
        closePanel() {
            this.$emit('close-panel');
            this.deletedEmailData();
            this.showPanel = false;
        },
        handleDelete() {
            this.isEditing = false;
            this.isDraft = false;
            this.deletedEmailData();
            this.resetRecipients();
        },
        resetRecipients() {
            this.deleteRecipients();
            this.addAutomaticContactsToRecipient();
        },
        deleteRecipients() {
            this.recipients = {
                to: [],
                cc: [],
                bcc: [],
            };
        },
        checkIsCurrentEmail(email) {
            return email.id === this.emailInfo.id;
        },
        formatProviderEmail(email) {
            return {
                ...email,
                created: email.date,
                linked_object: email.related[0],
            };
        },
        checkContactAlreadyExistsInContactsList(contact, contactsList) {
            return contactsList.some((existingContact) => existingContact.email === contact.email);
        },
        formattedSentDate(dateToFormat) {
            const date = moment(dateToFormat).format('ll');
            const time = moment(dateToFormat).format('LT');

            return this.$t.__('email.EmailConsultPanel.complete_date', {
                date: date,
                time: time,
            });
        },
        handleChange(emailData) {
            this.saveEmailData(emailData);
        },
        deletedEmailData() {
            localStorage.removeItem(`emailData_${this.hashPanel}`);
        },
        loadEmailData() {
            const emailData = localStorage.getItem(`emailData_${this.hashPanel}`);
            if (emailData) {
                const email = JSON.parse(emailData);
                this.isEditing = true;
                this.isDraft = true;
                this.emailInfo.subject = email.subject;
                this.replyType = email.replyType;
                this.replyContent = email.content;

                this.deleteRecipients();
                for (const address of email.addresses) {
                    if (address.type === 'cc') {
                        this.recipients.cc.push(address);
                    } else if (address.type === 'bcc') {
                        this.recipients.bcc.push(address);
                    } else {
                        this.recipients.to.push(address);
                    }
                }
                this.attachments = email.attachments;
            }
        },
        saveEmailData(emailData) {
            const cleanContent = this.removeAITags(emailData.email.content);
            localStorage.setItem(
                `emailData_${this.hashPanel}`,
                JSON.stringify({
                    addresses: emailData.email.addresses,
                    subject: emailData.email.subject,
                    content: cleanContent,
                    replyType: this.replyType,
                    attachments: emailData.attachments,
                }),
            );
        },
    },
};
</script>
<style lang="scss" scoped>
.email-panel__footer {
    padding-bottom: 0;

    .compass-panel__footer--default {
        flex-direction: row;
        justify-content: flex-end;
    }
}
.loader {
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate3d(-50%, -50%, 0);
}
</style>
